import React from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line

import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";
import { useTheme } from "../../contexts/ThemeContext.js";

import logo from "../../images/tanga-access-log-logo.png";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { ReactComponent as SunIcon } from "feather-icons/dist/icons/sun.svg";
import { ReactComponent as MoonIcon } from "feather-icons/dist/icons/moon.svg";

const Header = styled.header`
  ${tw`flex justify-between items-center max-w-screen-xl mx-auto`}
  background: var(--glass);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--glass-border);
  border-radius: 20px;
  padding: 10px 24px;
  box-shadow: 0 8px 32px var(--shadow);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 80px);
  max-width: 1200px;
  z-index: 1000;

  &:hover {
    box-shadow: 0 12px 40px var(--shadow);
    transform: translateX(-50%) translateY(-1px);
  }

  @media (max-width: 1279px) {
    width: calc(100% - 40px);
    top: 8px;
    padding: 8px 16px;
  }
`;

export const NavLink = styled.a`
  ${tw`text-sm font-semibold tracking-wide transition duration-300`}
  color: var(--text);
  position: relative;
  white-space: nowrap;
  padding-bottom: 2px;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--primary), var(--secondary));
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover::after {
    width: 100%;
  }

  &:hover {
    color: var(--primary);
    transform: translateY(-1px);
  }
`;

export const PrimaryLink = styled.a`
  ${tw`font-semibold tracking-wide transition duration-300`}
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: white !important;
  border-radius: 14px;
  padding: 7px 18px;
  font-size: 0.85rem;
  white-space: nowrap;
  display: inline-block;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);

  &:hover {
    transform: translateY(-2px) scale(1.04);
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.5);
    color: white;
  }
`;

export const LogoLink = styled.a`
  ${tw`flex items-center font-black text-base`}
  color: var(--text);
  white-space: nowrap;
  flex-shrink: 0;

  img {
    width: 22px;
    margin-right: 8px;
    border-radius: 50%;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
    transition: transform 0.3s ease;
    background: transparent;
    border: none;
  }

  &:hover img {
    transform: rotate(-5deg) scale(1.1);
  }

  &:hover {
    color: var(--primary);
  }
`;

const ThemeToggle = styled.button`
  ${tw`transition duration-300 flex items-center justify-center`}
  color: var(--text);
  background: var(--glass);
  backdrop-filter: blur(10px);
  border: 1px solid var(--glass-border);
  border-radius: 10px;
  padding: 6px;
  flex-shrink: 0;

  &:hover {
    color: var(--primary);
    background: var(--glass-border);
    transform: scale(1.1) rotate(15deg);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

/* Desktop nav only from xl (1280px) — 6 links need room */
export const DesktopNavLinks = styled.nav`
  ${tw`hidden xl:flex flex-1 items-center`}

  .nav-logo {
    flex-shrink: 0;
  }

  .nav-center {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;
  }

  .nav-center > * + * {
    margin-left: 1.25rem;
  }

  .nav-right {
    display: flex;
    flex-shrink: 0;
    align-items: center;
  }

  .nav-right > * + * {
    margin-left: 0.625rem;
  }
`;

export const NavToggle = styled.button`
  ${tw`xl:hidden z-20 focus:outline-none transition duration-300`}
  color: var(--text);
  background: var(--glass);
  backdrop-filter: blur(10px);
  border: 1px solid var(--glass-border);
  border-radius: 10px;
  padding: 8px;

  &:hover {
    color: var(--primary);
    background: var(--glass-border);
    transform: scale(1.1);
  }
`;

export const MobileNavLinksContainer = tw.nav`flex flex-1 items-center justify-between xl:hidden`;

export const MobileNavLinks = motion(styled.div`
  ${tw`xl:hidden z-30 fixed top-0 inset-x-0 mx-4 my-6 p-8 border text-center rounded-2xl`}
  background: var(--glass);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--glass-border);
  box-shadow: 0 8px 32px var(--shadow);
  color: var(--text);

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;

  a, button {
    margin: 0;
  }
`);

export default ({ roundedHeaderButton = false, logoLink, links, className, collapseBreakpointClass = "xl" }) => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = `/#${sectionId}`;
    }
  };

  const handleSectionClick = (e, sectionId) => {
    e.preventDefault();
    if (showNavLinks) toggleNavbar();
    scrollToSection(sectionId);
  };

  const defaultLogoLink = (
    <LogoLink href="/" className="nav-logo">
      <img src={logo} alt="Tanga Africa Logo" />
      Tanga Africa
    </LogoLink>
  );

  const defaultLinks = (
    <>
      <div className="nav-center">
        <NavLink href="/products">Produits</NavLink>
        <NavLink href="#usecases" onClick={(e) => handleSectionClick(e, 'usecases')}>
          Cas d'usage
        </NavLink>
        <NavLink href="#services" onClick={(e) => handleSectionClick(e, 'services')}>
          Services
        </NavLink>
        <NavLink href="#features" onClick={(e) => handleSectionClick(e, 'features')}>
          À Propos
        </NavLink>
        <NavLink href="#testimonials" onClick={(e) => handleSectionClick(e, 'testimonials')}>
          Témoignages
        </NavLink>
        <NavLink href="#faq" onClick={(e) => handleSectionClick(e, 'faq')}>
          FAQ
        </NavLink>
      </div>
      <div className="nav-right">
        <PrimaryLink href="https://dashboard.tangagroup.africa" target="_blank">
          Se connecter
        </PrimaryLink>
        <ThemeToggle onClick={toggleDarkMode} aria-label="Toggle dark mode">
          {isDarkMode ? <SunIcon /> : <MoonIcon />}
        </ThemeToggle>
      </div>
    </>
  );

  logoLink = logoLink || defaultLogoLink;
  links = links || defaultLinks;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <Header className={className || "header-light"}>
        {/* Desktop: single row — logo | center links | right actions */}
        <DesktopNavLinks>
          {logoLink}
          {links}
        </DesktopNavLinks>

        {/* Mobile / tablet: logo + hamburger */}
        <MobileNavLinksContainer>
          {defaultLogoLink}
          <MobileNavLinks
            initial={{ x: "150%", display: "none" }}
            animate={animation}
          >
            <NavLink href="/products" onClick={() => showNavLinks && toggleNavbar()}>Produits</NavLink>
            <NavLink href="#usecases" onClick={(e) => handleSectionClick(e, 'usecases')}>Cas d'usage</NavLink>
            <NavLink href="#services" onClick={(e) => handleSectionClick(e, 'services')}>Services</NavLink>
            <NavLink href="#features" onClick={(e) => handleSectionClick(e, 'features')}>À Propos</NavLink>
            <NavLink href="#testimonials" onClick={(e) => handleSectionClick(e, 'testimonials')}>Témoignages</NavLink>
            <NavLink href="#faq" onClick={(e) => handleSectionClick(e, 'faq')}>FAQ</NavLink>
            <PrimaryLink href="https://dashboard.tangagroup.africa" target="_blank" onClick={() => showNavLinks && toggleNavbar()}>
              Se connecter
            </PrimaryLink>
            <ThemeToggle onClick={toggleDarkMode} aria-label="Toggle dark mode">
              {isDarkMode ? <SunIcon /> : <MoonIcon />}
            </ThemeToggle>
          </MobileNavLinks>
          <NavToggle onClick={toggleNavbar} className={showNavLinks ? "open" : "closed"} aria-label="Toggle menu">
            {showNavLinks ? <CloseIcon style={{ width: 24, height: 24 }} /> : <MenuIcon style={{ width: 24, height: 24 }} />}
          </NavToggle>
        </MobileNavLinksContainer>
      </Header>
    </motion.div>
  );
};
